import {useStore} from 'zustand';
import {createStore, StoreApi} from 'zustand/vanilla';
import {useShallow} from 'zustand/react/shallow';
import React from 'react';
import {
    IntegratedRiskPicture,
    IrpBarrierAnalysisDTO,
    IrpNodeTabDTO,
    IrpReportLocationDTO,
    IrpReportTabDTO,
    IrpWorkbookOverviewDTO,
} from '../@types/integrated-risk-picture';
import {
    RecommendationCaseRelation,
    RegisterRecommendationDTO,
    SafetyRecommendationStatusGroup,
} from '../@types/safety-recommendation';
import EventSequence from '../@types/event-sequence';
import {DescriptorTerm} from '../@types/descriptor-term';

// ─── Public Types ─────────────────────────────────────────────────────────────

export type TabKey = 'overview' | 'skipped-reports' | 'precursor-barrier' | 'report-analysis' | 'barrier-effectiveness' | 'occurrence-patterns' | 'ntsb-recommendations';

export interface PersistedChatMessage {
    role: 'user' | 'assistant' | 'system';
    content: string;
}

/**
 * Lifecycle status of the per-node CFR (regulatory) analysis run.
 * undefined (absent from the map) = never started for this node.
 */
export type CfrAnalysisStatus = 'running' | 'done' | 'error';

/** Table filters and sort for the NTSB Recommendations register. Kept in the store so they survive tab switches. */
export interface NtsbRegisterFilters {
    /** Focus status groups (the FAA's own where it is an addressee); empty = every group. */
    statusGroups: SafetyRecommendationStatusGroup[];
    addressedTo: 'all' | 'faa' | 'notFaa';
    /** Tie to the workbook's reports; 'all' = either. */
    relation: 'all' | RecommendationCaseRelation;
    issuedFromYear: number | null;
    issuedToYear: number | null;
    showSuperseded: boolean;
    search: string;
    sortBy: 'issued' | 'yearsOpen' | 'number';
    sortDirection: 'asc' | 'desc';
}

export const DEFAULT_NTSB_REGISTER_FILTERS: NtsbRegisterFilters = {
    statusGroups: [],
    addressedTo: 'all',
    relation: 'all',
    issuedFromYear: null,
    issuedToYear: null,
    showSuperseded: false,
    search: '',
    sortBy: 'issued',
    sortDirection: 'desc',
};

/**
 * Primary filter state — sent to the backend on every BFF fetch.
 * selectedDescriptors contains descriptor term IDs (numbers, not names).
 */
export interface PrimaryFilter {
    dateFrom: Date | null;
    dateTo: Date | null;
    selectedDescriptors: Set<number>;
    /** Empty set = all sources included (no filter sent to backend). */
    selectedSources: Set<string>;
}

/**
 * Secondary date filter — applied client-side only (never sent to backend).
 * Set by Timeline bar-click; cleared by primary filter change or IRP change.
 */
export interface SecondaryDateFilter {
    dateFrom: Date | null;
    dateTo: Date | null;
}

/**
 * Cache entry for the precursor/barrier node tab.
 * Wraps IrpNodeTabDTO with the nodeId used to fetch it so consumers can
 * perform a stale-check: `nodeTabCache?.nodeId !== selectedNodeId`.
 */
export type NodeTabCacheEntry = IrpNodeTabDTO & { nodeId: number };

// ─── Store Interface ──────────────────────────────────────────────────────────

interface IRPWorkbookStore {
    // IRP identity
    irpId: number | null;

    // Shared data — loaded once per IRP, not cleared by primary filter changes
    irpDetails: IntegratedRiskPicture | null;
    /** undefined = loading, null = fetch failed / no data, EventSequence = loaded */
    eventSequenceData: EventSequence | null | undefined;
    availableDescriptors: DescriptorTerm[];
    availableSources: string[];

    // Navigation
    activeTab: TabKey;
    selectedNodeId: number | null;   // number to match backend Long nodeId
    selectedReportId: number | null;
    /** Cross-tab search seed: when set, OccurrencePatternsTab consumes this on mount
     *  by pre-populating its cluster-search field (and clearing this back to null).
     *  Lets BE → Report Clustering navigation reuse the existing search UX rather
     *  than introducing a separate filter chip. */
    pendingClusterSearch: string | null;

    // Filters
    primaryFilter: PrimaryFilter;
    secondaryDateFilter: SecondaryDateFilter | null;

    // BFF tab caches — cleared whenever irpId or primaryFilter changes
    overviewCache: IrpWorkbookOverviewDTO | null;
    reportLocationsCache: IrpReportLocationDTO[] | null;
    nodeTabCache: NodeTabCacheEntry | null;
    reportTabCache: IrpReportTabDTO | null;
    barrierAnalysisCache: IrpBarrierAnalysisDTO | null;
    skippedReportsPagination: { page: number; size: 10 | 25 | 50; search: string };

    // Chat state — persisted per node, keyed by string nodeId
    nodeChatHistory: Map<string, PersistedChatMessage[]>;
    nodeChatContextSent: Map<string, boolean>;

    // CFR (regulatory) analysis thread — a separate per-node conversation from the
    // general chat. nodeCfrStatus tracks the auto-run lifecycle so the lookup fires
    // at most once per node.
    nodeCfrHistory: Map<string, PersistedChatMessage[]>;
    nodeCfrStatus: Map<string, CfrAnalysisStatus>;
    /** Backend CFR analysis run id per node, so polling resumes after tab/view switches. */
    nodeCfrRunId: Map<string, string>;

    // NTSB Recommendations register: the recommendations tied to the workbook's reports
    // (cleared with the other BFF caches on a primary filter change), its table filters, and
    // its one conversation, which is about whatever the filters currently show.
    ntsbRecommendationsCache: RegisterRecommendationDTO[] | null;
    ntsbRegisterFilters: NtsbRegisterFilters;
    ntsbChatHistory: PersistedChatMessage[];
    ntsbChatContextSent: boolean;

    // Dendrogram UI state — survives tab switches and fullscreen toggling; cleared on IRP change
    /** null = never rendered yet, use default depth-2 expansion */
    dendrogramCollapsedNodeIds: Set<number> | null;
    dendrogramTransform: { x: number; y: number; k: number } | null;

    // === Actions ===

    /** Switch to a new IRP. Clears all caches and resets navigation state. */
    setIrpId: (id: number) => void;

    /**
     * Atomically initialize the workbook for a new IRP with an optional primary filter.
     * Combines setIrpId + setPrimaryFilter into a single store update to avoid
     * triggering two separate cache-clear cycles (and therefore two API fetches).
     */
    initializeWorkbook: (id: number, filter?: PrimaryFilter) => void;

    setIrpDetails: (details: IntegratedRiskPicture) => void;
    setEventSequenceData: (esd: EventSequence | null | undefined) => void;
    setAvailableDescriptors: (terms: DescriptorTerm[]) => void;
    setAvailableSources: (sources: string[]) => void;

    /** Update primary filter and invalidate all 4 BFF caches. */
    setPrimaryFilter: (filter: PrimaryFilter) => void;

    /**
     * Pre-populate the date pickers from overview data on initial workbook open.
     * Does NOT clear any caches — the data was already fetched unfiltered, so the
     * full date range is equivalent to no filter for the current report set.
     * Should be called at most once per IRP load (guarded by a ref in OverviewTab).
     */
    setInitialDateRange: (dateFrom: Date, dateTo: Date) => void;

    /** Update secondary date filter only — does NOT clear any caches. */
    setSecondaryDateFilter: (filter: SecondaryDateFilter | null) => void;

    setOverviewCache: (data: IrpWorkbookOverviewDTO) => void;
    setReportLocationsCache: (data: IrpReportLocationDTO[]) => void;
    /** Stores data with its nodeId for stale-checking. */
    setNodeTabCache: (nodeId: number, data: IrpNodeTabDTO) => void;
    setReportTabCache: (data: IrpReportTabDTO) => void;
    setBarrierAnalysisCache: (data: IrpBarrierAnalysisDTO) => void;
    setSkippedReportsPagination: (pagination: { page?: number; size?: 10 | 25 | 50; search?: string }) => void;

    setActiveTab: (tab: TabKey) => void;
    setSelectedNode: (nodeId: number | null) => void;
    setSelectedReport: (reportId: number | null) => void;

    navigateToReportAnalysis: (reportId: number) => void;
    navigateToPrecursorBarrier: (nodeId: number) => void;
    /** Jump to OccurrencePatterns and seed its cluster search field with the given
     *  term (e.g. a barrier uniqueId). Tab consumes the seed once and clears it. */
    navigateToOccurrencePatternsWithSearch: (searchTerm: string) => void;
    /** Clear the pending cluster search (Tab calls this after consuming it). */
    setPendingClusterSearch: (term: string | null) => void;

    // Chat actions
    setNodeChatHistory: (nodeId: string, messages: PersistedChatMessage[]) => void;
    getNodeChatHistory: (nodeId: string) => PersistedChatMessage[];
    clearNodeChatHistory: (nodeId: string) => void;
    setNodeChatContextSent: (nodeId: string, sent: boolean) => void;
    getNodeChatContextSent: (nodeId: string) => boolean;

    // CFR analysis actions
    setNodeCfrHistory: (nodeId: string, messages: PersistedChatMessage[]) => void;
    getNodeCfrHistory: (nodeId: string) => PersistedChatMessage[];
    clearNodeCfrHistory: (nodeId: string) => void;
    setNodeCfrStatus: (nodeId: string, status: CfrAnalysisStatus) => void;
    getNodeCfrStatus: (nodeId: string) => CfrAnalysisStatus | undefined;
    setNodeCfrRunId: (nodeId: string, runId: string) => void;
    getNodeCfrRunId: (nodeId: string) => string | undefined;

    // NTSB Recommendations register actions
    setNtsbRecommendationsCache: (data: RegisterRecommendationDTO[]) => void;
    setNtsbRegisterFilters: (filters: NtsbRegisterFilters) => void;
    setNtsbChatHistory: (messages: PersistedChatMessage[]) => void;
    setNtsbChatContextSent: (sent: boolean) => void;

    setDendrogramCollapsedNodeIds: (ids: Set<number>) => void;
    setDendrogramTransform: (transform: { x: number; y: number; k: number } | null) => void;

    reset: () => void;
}

// ─── Initial State Factory ────────────────────────────────────────────────────

/**
 * Returns a fresh initial state object.
 * Called as a factory so each reset creates new Set/Map instances
 * instead of sharing the same mutable references.
 */
const createInitialState = (): Omit<
    IRPWorkbookStore,
    | 'setIrpId' | 'initializeWorkbook' | 'setIrpDetails' | 'setEventSequenceData' | 'setAvailableDescriptors' | 'setAvailableSources'
    | 'setPrimaryFilter' | 'setInitialDateRange' | 'setSecondaryDateFilter'
    | 'setOverviewCache' | 'setReportLocationsCache' | 'setNodeTabCache' | 'setReportTabCache' | 'setBarrierAnalysisCache'
    | 'setActiveTab' | 'setSelectedNode' | 'setSelectedReport'
    | 'navigateToReportAnalysis' | 'navigateToPrecursorBarrier'
    | 'navigateToOccurrencePatternsWithSearch' | 'setPendingClusterSearch'
    | 'setSkippedReportsPagination'
    | 'setNodeChatHistory' | 'getNodeChatHistory' | 'clearNodeChatHistory'
    | 'setNodeChatContextSent' | 'getNodeChatContextSent'
    | 'setNodeCfrHistory' | 'getNodeCfrHistory' | 'clearNodeCfrHistory'
    | 'setNodeCfrStatus' | 'getNodeCfrStatus'
    | 'setNodeCfrRunId' | 'getNodeCfrRunId'
    | 'setNtsbRecommendationsCache' | 'setNtsbRegisterFilters' | 'setNtsbChatHistory' | 'setNtsbChatContextSent'
    | 'setDendrogramCollapsedNodeIds' | 'setDendrogramTransform'
    | 'reset'
> => ({
    irpId: null,
    irpDetails: null,
    eventSequenceData: null,
    availableDescriptors: [],
    availableSources: [],
    activeTab: 'overview' as TabKey,
    selectedNodeId: null,
    selectedReportId: null,
    pendingClusterSearch: null,
    primaryFilter: {
        dateFrom: null,
        dateTo: null,
        selectedDescriptors: new Set<number>(),
        selectedSources: new Set<string>(),
    },
    secondaryDateFilter: null,
    overviewCache: null,
    reportLocationsCache: null,
    nodeTabCache: null,
    reportTabCache: null,
    barrierAnalysisCache: null,
    skippedReportsPagination: { page: 0, size: 10 as 10 | 25 | 50, search: '' },
    nodeChatHistory: new Map<string, PersistedChatMessage[]>(),
    nodeChatContextSent: new Map<string, boolean>(),
    nodeCfrHistory: new Map<string, PersistedChatMessage[]>(),
    nodeCfrStatus: new Map<string, CfrAnalysisStatus>(),
    nodeCfrRunId: new Map<string, string>(),
    ntsbRecommendationsCache: null,
    ntsbRegisterFilters: DEFAULT_NTSB_REGISTER_FILTERS,
    ntsbChatHistory: [],
    ntsbChatContextSent: false,
    dendrogramCollapsedNodeIds: null,
    dendrogramTransform: null,
});

/**
 * Fields cleared when irpId or primaryFilter changes.
 * nodeTabCache and reportTabCache are intentionally excluded:
 *   - nodeTabCache: date range does not apply to the node tab endpoint
 *   - reportTabCache: single-report view has no date-range context
 * Both are still cleared on irpId change (via setIrpId / initializeWorkbook).
 */
const CLEARED_CACHES = {
    overviewCache: null,
    reportLocationsCache: null,
    barrierAnalysisCache: null,
    ntsbRecommendationsCache: null,
    skippedReportsPagination: { page: 0, size: 10 as 10, search: '' },
} as const;

/** All caches cleared — used only when switching to a different IRP. */
const ALL_CACHES_CLEARED = {
    ...CLEARED_CACHES,
    nodeTabCache: null,
    reportTabCache: null,
    availableSources: [] as string[],
} as const;

// ─── Store Factory ────────────────────────────────────────────────────────────

/**
 * Creates a new isolated store instance for a single IRP workbook tab.
 * Each open IRP workbook gets its own instance so filters, caches, and
 * navigation state are fully independent between tabs.
 */
export type IRPWorkbookStoreInstance = StoreApi<IRPWorkbookStore>;

export const createIRPWorkbookStore = (): IRPWorkbookStoreInstance =>
    createStore<IRPWorkbookStore>((set, get) => ({
        ...createInitialState(),

        setIrpId: (id): void => set({
            irpId: id,
            irpDetails: null,
            eventSequenceData: undefined, // undefined = loading; null = fetch done, no data
            selectedNodeId: null,
            selectedReportId: null,
            pendingClusterSearch: null,
            secondaryDateFilter: null,
            activeTab: 'overview' as TabKey,
            dendrogramCollapsedNodeIds: null,
            dendrogramTransform: null,
            ...ALL_CACHES_CLEARED,
        }),

        initializeWorkbook: (id, filter): void => set({
            irpId: id,
            irpDetails: null,
            eventSequenceData: undefined,
            selectedNodeId: null,
            selectedReportId: null,
            pendingClusterSearch: null,
            secondaryDateFilter: null,
            activeTab: 'overview' as TabKey,
            primaryFilter: filter ?? {
                dateFrom: null,
                dateTo: null,
                selectedDescriptors: new Set<number>(),
                selectedSources: new Set<string>(),
            },
            dendrogramCollapsedNodeIds: null,
            dendrogramTransform: null,
            ...ALL_CACHES_CLEARED,
        }),

        setIrpDetails: (details): void => set({ irpDetails: details }),

        setEventSequenceData: (esd): void => set({ eventSequenceData: esd }),

        setAvailableDescriptors: (terms): void => set({ availableDescriptors: terms }),

        setAvailableSources: (sources): void => set({ availableSources: sources }),

        setPrimaryFilter: (filter): void => set({
            primaryFilter: filter,
            secondaryDateFilter: null,
            ...CLEARED_CACHES,
        }),

        setInitialDateRange: (dateFrom, dateTo): void => set((state) => ({
            primaryFilter: { ...state.primaryFilter, dateFrom, dateTo },
        })),

        setSecondaryDateFilter: (filter): void => set({ secondaryDateFilter: filter }),

        setOverviewCache: (data): void => set({ overviewCache: data }),

        setReportLocationsCache: (data): void => set({ reportLocationsCache: data }),

        setNodeTabCache: (nodeId, data): void => set({ nodeTabCache: { nodeId, ...data } }),

        setReportTabCache: (data): void => set({ reportTabCache: data }),

        setBarrierAnalysisCache: (data): void => set({ barrierAnalysisCache: data }),

        setSkippedReportsPagination: (pagination): void => set((state) => ({
            skippedReportsPagination: { ...state.skippedReportsPagination, ...pagination },
        })),

        setActiveTab: (tab): void => set({ activeTab: tab }),

        setSelectedNode: (nodeId): void => set({ selectedNodeId: nodeId }),

        setSelectedReport: (reportId): void => set({ selectedReportId: reportId }),

        navigateToReportAnalysis: (reportId): void => set({
            activeTab: 'report-analysis' as TabKey,
            selectedReportId: reportId,
        }),

        navigateToPrecursorBarrier: (nodeId): void => set({
            activeTab: 'precursor-barrier' as TabKey,
            selectedNodeId: nodeId,
        }),

        navigateToOccurrencePatternsWithSearch: (searchTerm): void => set({
            activeTab: 'occurrence-patterns' as TabKey,
            pendingClusterSearch: searchTerm,
        }),

        setPendingClusterSearch: (term): void => set({ pendingClusterSearch: term }),

        // Chat actions
        setNodeChatHistory: (nodeId, messages): void => set((state) => {
            const newHistory = new Map(state.nodeChatHistory);
            newHistory.set(nodeId, messages);
            return { nodeChatHistory: newHistory };
        }),

        getNodeChatHistory: (nodeId): PersistedChatMessage[] =>
            get().nodeChatHistory.get(nodeId) || [],

        clearNodeChatHistory: (nodeId): void => set((state) => {
            const newHistory = new Map(state.nodeChatHistory);
            newHistory.delete(nodeId);
            const newContextSent = new Map(state.nodeChatContextSent);
            newContextSent.delete(nodeId);
            return { nodeChatHistory: newHistory, nodeChatContextSent: newContextSent };
        }),

        setNodeChatContextSent: (nodeId, sent): void => set((state) => {
            const newContextSent = new Map(state.nodeChatContextSent);
            newContextSent.set(nodeId, sent);
            return { nodeChatContextSent: newContextSent };
        }),

        getNodeChatContextSent: (nodeId): boolean =>
            get().nodeChatContextSent.get(nodeId) || false,

        // CFR analysis actions
        setNodeCfrHistory: (nodeId, messages): void => set((state) => {
            const newHistory = new Map(state.nodeCfrHistory);
            newHistory.set(nodeId, messages);
            return { nodeCfrHistory: newHistory };
        }),

        getNodeCfrHistory: (nodeId): PersistedChatMessage[] =>
            get().nodeCfrHistory.get(nodeId) || [],

        clearNodeCfrHistory: (nodeId): void => set((state) => {
            const newHistory = new Map(state.nodeCfrHistory);
            newHistory.delete(nodeId);
            const newStatus = new Map(state.nodeCfrStatus);
            newStatus.delete(nodeId);
            const newRunIds = new Map(state.nodeCfrRunId);
            newRunIds.delete(nodeId);
            return { nodeCfrHistory: newHistory, nodeCfrStatus: newStatus, nodeCfrRunId: newRunIds };
        }),

        setNodeCfrStatus: (nodeId, status): void => set((state) => {
            const newStatus = new Map(state.nodeCfrStatus);
            newStatus.set(nodeId, status);
            return { nodeCfrStatus: newStatus };
        }),

        setNodeCfrRunId: (nodeId, runId): void => set((state) => {
            const newRunIds = new Map(state.nodeCfrRunId);
            newRunIds.set(nodeId, runId);
            return { nodeCfrRunId: newRunIds };
        }),

        getNodeCfrRunId: (nodeId): string | undefined =>
            get().nodeCfrRunId.get(nodeId),

        getNodeCfrStatus: (nodeId): CfrAnalysisStatus | undefined =>
            get().nodeCfrStatus.get(nodeId),

        // NTSB Recommendations register actions
        setNtsbRecommendationsCache: (data): void => set({ ntsbRecommendationsCache: data }),
        setNtsbRegisterFilters: (filters): void => set({ ntsbRegisterFilters: filters }),
        setNtsbChatHistory: (messages): void => set({ ntsbChatHistory: messages }),
        setNtsbChatContextSent: (sent): void => set({ ntsbChatContextSent: sent }),

        setDendrogramCollapsedNodeIds: (ids): void => set({ dendrogramCollapsedNodeIds: ids }),
        setDendrogramTransform: (transform): void => set({ dendrogramTransform: transform }),

        reset: (): void => set(createInitialState()),
    }));

// ─── Context ──────────────────────────────────────────────────────────────────

/**
 * React context that holds the store instance for the nearest IRPWorkbookProvider.
 * Each provider creates its own store so multiple open IRP tabs are fully isolated.
 */
export const IRPWorkbookStoreContext = React.createContext<IRPWorkbookStoreInstance | null>(null);

// ─── Hooks ────────────────────────────────────────────────────────────────────

/**
 * Primary selector hook. Reads from the nearest IRPWorkbookProvider's store instance.
 * Drop-in replacement for the previous global Zustand store hook.
 */
export const useIRPWorkbookStore = <T>(selector: (state: IRPWorkbookStore) => T): T => {
    const store = React.useContext(IRPWorkbookStoreContext);
    if (!store) throw new Error('useIRPWorkbookStore must be used within IRPWorkbookProvider');
    return useStore(store, selector);
};

/**
 * Returns the raw store instance from context.
 * Use this when you need `store.getState()` inside effects to avoid stale closures.
 */
export const useIRPWorkbookStoreApi = (): IRPWorkbookStoreInstance => {
    const store = React.useContext(IRPWorkbookStoreContext);
    if (!store) throw new Error('useIRPWorkbookStoreApi must be used within IRPWorkbookProvider');
    return store;
};

// ─── Selector Hooks ───────────────────────────────────────────────────────────

export const useIRPDetails = (): IntegratedRiskPicture | null =>
    useIRPWorkbookStore((state) => state.irpDetails);

export const useEventSequenceData = (): EventSequence | null | undefined =>
    useIRPWorkbookStore((state) => state.eventSequenceData);

export const useAvailableDescriptors = (): DescriptorTerm[] =>
    useIRPWorkbookStore((state) => state.availableDescriptors);

export const useAvailableSources = (): string[] =>
    useIRPWorkbookStore((state) => state.availableSources);

export const useActiveTab = (): TabKey =>
    useIRPWorkbookStore((state) => state.activeTab);

export const useSelectedNodeId = (): number | null =>
    useIRPWorkbookStore((state) => state.selectedNodeId);

export const useSelectedReportId = (): number | null =>
    useIRPWorkbookStore((state) => state.selectedReportId);

export const usePendingClusterSearch = (): string | null =>
    useIRPWorkbookStore((state) => state.pendingClusterSearch);

export const usePrimaryFilter = (): PrimaryFilter =>
    useIRPWorkbookStore((state) => state.primaryFilter);

export const useSecondaryDateFilter = (): SecondaryDateFilter | null =>
    useIRPWorkbookStore(useShallow((state) => state.secondaryDateFilter));

export const useOverviewCache = (): IrpWorkbookOverviewDTO | null =>
    useIRPWorkbookStore((state) => state.overviewCache);

export const useReportLocationsCache = (): IrpReportLocationDTO[] | null =>
    useIRPWorkbookStore((state) => state.reportLocationsCache);

export const useNodeTabCache = (): NodeTabCacheEntry | null =>
    useIRPWorkbookStore((state) => state.nodeTabCache);

export const useReportTabCache = (): IrpReportTabDTO | null =>
    useIRPWorkbookStore((state) => state.reportTabCache);

export const useBarrierAnalysisCache = (): IrpBarrierAnalysisDTO | null =>
    useIRPWorkbookStore((state) => state.barrierAnalysisCache);

export const useSkippedReportsPagination = (): { page: number; size: 10 | 25 | 50; search: string } =>
    useIRPWorkbookStore((state) => state.skippedReportsPagination);

// Chat selectors
export const useNodeChatHistory = (nodeId: string | null): PersistedChatMessage[] => {
    const chatHistory = useIRPWorkbookStore((state) => state.nodeChatHistory);
    if (!nodeId) return [];
    return chatHistory.get(nodeId) || [];
};

export const useNodeChatContextSent = (nodeId: string | null): boolean => {
    const contextSent = useIRPWorkbookStore((state) => state.nodeChatContextSent);
    if (!nodeId) return false;
    return contextSent.get(nodeId) || false;
};

// CFR analysis selectors
export const useNodeCfrHistory = (nodeId: string | null): PersistedChatMessage[] => {
    const cfrHistory = useIRPWorkbookStore((state) => state.nodeCfrHistory);
    if (!nodeId) return [];
    return cfrHistory.get(nodeId) || [];
};

export const useNodeCfrStatus = (nodeId: string | null): CfrAnalysisStatus | undefined => {
    const cfrStatus = useIRPWorkbookStore((state) => state.nodeCfrStatus);
    if (!nodeId) return undefined;
    return cfrStatus.get(nodeId);
};

// NTSB Recommendations register selectors
export const useNtsbRecommendationsCache = (): RegisterRecommendationDTO[] | null =>
    useIRPWorkbookStore((state) => state.ntsbRecommendationsCache);

export const useNtsbRegisterFilters = (): NtsbRegisterFilters =>
    useIRPWorkbookStore((state) => state.ntsbRegisterFilters);
